import { cloneDeep } from 'lodash'
import { ethAddress, type Address } from 'viem'
import type { Config } from 'wagmi'
import type { StoreApi } from 'zustand'
import type {
  FormEstGas,
  FormStatus,
  FormValues,
  RawRoutesAndOutput,
  SearchedParams,
} from '@/dex/components/PageRouterSwap/types'
import { DEFAULT_FORM_STATUS, DEFAULT_FORM_VALUES } from '@/dex/components/PageRouterSwap/utils'
import { curvejsApi } from '@/dex/lib/curvejs'
import type { State } from '@/dex/store/useStore'
import { CurveApi, FnStepApproveResponse, FnStepResponse } from '@/dex/types/main.types'
import { getMaxAmountMinusGas } from '@/dex/utils/utilsGasPrices'
import { useWallet } from '@evm-ui/features/connect-wallet'
import { fetchTokenBalance } from '@evm-ui/hooks/useTokenBalance'
import { fetchGasInfoAndUpdateLib } from '@evm-ui/queries/gas-info.query'
import { setMissingProvider } from '@evm-ui/utils/store.util'
import type { Decimal } from '@primitives/decimal.utils'
import { sleep } from '@primitives/promise.utils'
import { SLIPPAGE } from '@ui/features/forms/slippage/slippage.utils'

type StateKey = keyof typeof DEFAULT_STATE

type SliceState = {
  activeKey: string
  formEstGas: Record<string, FormEstGas>
  formStatus: FormStatus
  formValues: FormValues
  isMaxLoading: boolean
  routesAndOutput: Record<string, RawRoutesAndOutput>
}

const SLICE_KEY = 'quickSwap'

/** Before we have any route, we cannot choose between crypto and stable slippage. Default to the higher one. **/
const DEFAULT_SLIPPAGE = SLIPPAGE.crypto.default

export type QuickSwapSlice = {
  [SLICE_KEY]: SliceState & {
    fetchMaxAmount: (
      config: Config,
      curve: CurveApi,
      searchedParams: SearchedParams,
      maxSlippage: string,
    ) => Promise<void>
    fetchRoutesAndOutput: (
      config: Config,
      curve: CurveApi,
      searchedParams: SearchedParams,
      maxSlippage: string,
    ) => Promise<void>
    fetchEstGasApproval: (curve: CurveApi, searchedParams: SearchedParams, maxSlippage: string) => Promise<void>
    resetFormErrors: () => void
    setFormValues: (
      config: Config,
      curve: CurveApi | null,
      updatedFormValues: Partial<FormValues>,
      searchedParams: SearchedParams,
      maxSlippage: Decimal | undefined,
      isGetMaxFrom?: boolean,
      isFullReset?: boolean,
      isRefetch?: boolean,
    ) => Promise<void>

    // steps
    fetchStepApprove: (
      activeKey: string,
      config: Config,
      curve: CurveApi,
      formValues: FormValues,
      searchedParams: SearchedParams,
      maxSlippage: Decimal,
    ) => Promise<FnStepApproveResponse | undefined>
    fetchStepSwap: (
      activeKey: string,
      config: Config,
      curve: CurveApi,
      formValues: FormValues,
      searchedParams: SearchedParams,
      maxSlippage: Decimal,
    ) => Promise<(FnStepResponse & { swappedAmount: string }) | undefined>

    setStateByActiveKey: <T>(key: StateKey, activeKey: string, value: T) => void
    setStateByKey: <T>(key: StateKey, value: T) => void
    setStateByKeys: (SliceState: Partial<SliceState>) => void
    resetState: () => void
  }
}

const DEFAULT_STATE: SliceState = {
  activeKey: '',
  formEstGas: {},
  formStatus: DEFAULT_FORM_STATUS,
  formValues: DEFAULT_FORM_VALUES,
  isMaxLoading: false,
  routesAndOutput: {},
}

export const createQuickSwapSlice = (
  _set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): QuickSwapSlice => ({
  [SLICE_KEY]: {
    ...DEFAULT_STATE,

    fetchMaxAmount: async (config, curve, searchedParams, maxSlippage) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      const { chainId, signerAddress } = curve
      const { fromAddress, toAddress } = searchedParams

      const cFormValues = cloneDeep(sliceState.formValues)

      if (signerAddress) {
        const userBalance = await fetchTokenBalance(config, {
          chainId,
          userAddress: signerAddress,
          tokenAddress: fromAddress as Address,
        })

        cFormValues.fromAmount = userBalance
        const activeKey = getRouterActiveKey(curve, cFormValues, searchedParams, maxSlippage)

        // get max amount for native token
        if (fromAddress.toLowerCase() === ethAddress) {
          const { basePlusPriority } = await fetchGasInfoAndUpdateLib({ chainId })
          const firstBasePlusPriority = basePlusPriority?.[0]

          if (typeof firstBasePlusPriority !== 'undefined' && +userBalance > 0) {
            sliceState.setStateByKey('isMaxLoading', true)
            // must call routesAndOutput first before estGas
            await curvejsApi.router.routesAndOutput(activeKey, curve, cFormValues, searchedParams)

            const resp = await curvejsApi.router.estGasApproval(
              activeKey,
              curve,
              fromAddress,
              toAddress,
              userBalance,
              maxSlippage,
            )

            if (resp.estimatedGas) {
              cFormValues.fromAmount = getMaxAmountMinusGas(resp.estimatedGas, firstBasePlusPriority, userBalance)
            }
          }
        }
      } else {
        cFormValues.fromAmount = ''
      }

      sliceState.setStateByKeys({
        activeKey: getRouterActiveKey(curve, cFormValues, searchedParams, maxSlippage),
        formValues: cFormValues,
        isMaxLoading: false,
      })
    },
    fetchRoutesAndOutput: async (config, curve, searchedParams, maxSlippage) => {
      const state = get()
      const sliceState = state[SLICE_KEY]
      const activeKey = sliceState.activeKey

      const cFormValues = cloneDeep(sliceState.formValues)
      const cFormStatus = cloneDeep(sliceState.formStatus)

      if ((cFormValues.isFrom && +cFormValues.fromAmount <= 0) || (!cFormValues.isFrom && +cFormValues.toAmount <= 0))
        return

      if (!curve.signerAddress) return // If no signer, routing handled via `useRouterApi`

      // allow UI to paint first
      await sleep(100)
      const { exchangeRates, ...resp } = await curvejsApi.router.routesAndOutput(
        activeKey,
        curve,
        cFormValues,
        searchedParams,
      )

      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          // update to/from form values
          if (cFormValues.isFrom) {
            cFormValues.toAmount = resp.toAmount
          } else {
            cFormValues.fromAmount = resp.fromAmount
          }

          const cActiveKey = getRouterActiveKey(curve, cFormValues, searchedParams, maxSlippage)
          cFormStatus.error = resp.routes.length === 0 ? 'error-swap-not-available' : ''

          sliceState.setStateByKeys({
            activeKey: cActiveKey,
            formValues: cloneDeep(cFormValues),
            formStatus: cFormStatus,
            routesAndOutput: {
              [cActiveKey]: {
                ...resp,
                router: 'curve',
                loading: false,
                exchangeRates: [exchangeRates[0] ?? '0', exchangeRates[1] ?? '0'] as [Decimal, Decimal],
              },
            },
          })

          // validation
          const fromAmount = await fetchTokenBalance(config, {
            chainId: curve.chainId,
            userAddress: curve.signerAddress,
            tokenAddress: searchedParams.fromAddress as Address,
          })
          cFormValues.fromError = +cFormValues.fromAmount > +fromAmount ? 'too-much' : ''
          get()[SLICE_KEY].setStateByKey('formValues', cFormValues)
        }
      }
    },
    fetchEstGasApproval: async (curve, searchedParams, maxSlippage) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      const activeKey = sliceState.activeKey
      const { signerAddress } = curve
      const { fromAmount } = sliceState.formValues
      const { fromAddress, toAddress } = searchedParams

      if (+fromAmount <= 0 || !signerAddress) return

      // api call
      const resp = await curvejsApi.router.estGasApproval(
        activeKey,
        curve,
        fromAddress,
        toAddress,
        fromAmount,
        maxSlippage,
      )

      // set estimate gas state
      sliceState.setStateByKey('formEstGas', { [activeKey]: { estimatedGas: resp.estimatedGas, loading: false } })

      // update form status
      const storedFormStatus = get()[SLICE_KEY].formStatus

      if (storedFormStatus.formProcessing) return

      sliceState.setStateByKey('formStatus', {
        ...storedFormStatus,
        isApproved: resp.isApproved,
        error: storedFormStatus.error || resp.error,
      })
    },
    resetFormErrors: () => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      sliceState.setStateByKeys({
        formStatus: { ...sliceState.formStatus, error: '', swapError: '' },
        formValues: { ...sliceState.formValues, fromError: '' },
      })
    },
    setFormValues: async (
      config,
      curve,
      updatedFormValues,
      searchedParams,
      maxSlippage,
      isGetMaxFrom,
      isFullReset,
      isRefetch, // x
    ) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      // stored values
      const storedFormStatus = sliceState.formStatus
      const storedFormValues = sliceState.formValues

      // update formStatus, form values, reset errors
      const cFormValues = cloneDeep(
        isRefetch
          ? storedFormValues
          : isFullReset
            ? { ...storedFormValues, isFrom: true, fromAmount: '', fromError: '' as const, toAmount: '' }
            : { ...storedFormValues, ...updatedFormValues, fromError: '' as const },
      )

      const activeKey = getRouterActiveKey(curve, cFormValues, searchedParams, maxSlippage)
      const cFormStatus = cloneDeep(isRefetch ? { ...storedFormStatus, swapError: '' } : DEFAULT_FORM_STATUS)

      get()[SLICE_KEY].setStateByKeys({
        activeKey,
        formValues: cloneDeep(cFormValues),
        formStatus: cloneDeep(cFormStatus),
      })

      if (!curve || !searchedParams.fromAddress || !searchedParams.toAddress) return

      // set loading
      const storedRoutesAndOutput = sliceState.routesAndOutput[activeKey]
      if (storedRoutesAndOutput) {
        sliceState.setStateByKey('routesAndOutput', { [activeKey]: { ...storedRoutesAndOutput, loading: true } })
      }

      // Refetch balances
      if (curve.signerAddress) {
        if (searchedParams.fromAddress) {
          await fetchTokenBalance(config, {
            chainId: curve.chainId,
            userAddress: curve.signerAddress,
            tokenAddress: searchedParams.fromAddress as Address,
          })
        }

        if (searchedParams.toAddress) {
          await fetchTokenBalance(config, {
            chainId: curve.chainId,
            userAddress: curve.signerAddress,
            tokenAddress: searchedParams.toAddress as Address,
          })
        }
      }

      // get max if MAX button is clicked
      maxSlippage ??= DEFAULT_SLIPPAGE
      if (isGetMaxFrom) await sliceState.fetchMaxAmount(config, curve, searchedParams, maxSlippage)

      // api calls
      await sliceState.fetchRoutesAndOutput(config, curve, searchedParams, maxSlippage)
      void sliceState.fetchEstGasApproval(curve, searchedParams, maxSlippage)
    },

    // steps
    fetchStepApprove: async (activeKey, config, curve, formValues, searchedParams, maxSlippage) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      sliceState.setStateByKey('formStatus', {
        ...sliceState.formStatus,
        formProcessing: true,
        swapError: '',
        step: 'APPROVAL',
      })

      const { fromAmount } = formValues
      const { fromAddress } = searchedParams

      const resp = await curvejsApi.router.swapApprove(activeKey, curve, provider, fromAddress, fromAmount)

      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(sliceState.formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.swapError = ''

        if (resp.error) {
          cFormStatus.swapError = resp.error
          sliceState.setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'APPROVE'
          cFormStatus.isApproved = true
          sliceState.setStateByKey('formStatus', cFormStatus)

          // re-fetch est gas, approval, routes and output
          maxSlippage ??= DEFAULT_SLIPPAGE
          await sliceState.fetchRoutesAndOutput(config, curve, searchedParams, maxSlippage)
          void sliceState.fetchEstGasApproval(curve, searchedParams, maxSlippage)
        }

        return resp
      }
    },
    fetchStepSwap: async (activeKey, config, curve, formValues, searchedParams, maxSlippage) => {
      const state = get()
      const sliceState = state[SLICE_KEY]

      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        swapError: '',
        step: 'SWAP',
      })

      const { fromAmount } = formValues
      const { fromAddress, toAddress } = searchedParams

      const resp = await curvejsApi.router.swap(
        activeKey,
        curve,
        provider,
        fromAddress,
        fromAmount,
        toAddress,
        maxSlippage,
      )

      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(sliceState.formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.swapError = ''

        if (resp.error) {
          cFormStatus.swapError = resp.error
          sliceState.setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'SWAP'

          const cFormValues = cloneDeep(formValues)
          cFormValues.fromAmount = ''
          cFormValues.toAmount = ''

          get()[SLICE_KEY].setStateByKeys({
            activeKey: getRouterActiveKey(curve, cFormValues, searchedParams, maxSlippage),
            formEstGas: {},
            formValues: cloneDeep(cFormValues),
            formStatus: cFormStatus,
            routesAndOutput: {},
          })

          // Refetch balances
          await fetchTokenBalance(config, {
            chainId: curve.chainId,
            userAddress: curve.signerAddress,
            tokenAddress: fromAddress as Address,
          })

          await fetchTokenBalance(config, {
            chainId: curve.chainId,
            userAddress: curve.signerAddress,
            tokenAddress: toAddress as Address,
          })
        }

        return resp
      }
    },

    // slice helpers
    setStateByActiveKey: (key, activeKey, value) => {
      if (Object.keys(get()[SLICE_KEY][key] as object).length > 30) {
        get().setAppStateByKey(SLICE_KEY, key, { [activeKey]: value })
      } else {
        get().setAppStateByActiveKey(SLICE_KEY, key, activeKey, value)
      }
    },
    setStateByKey: (key, value) => {
      get().setAppStateByKey(SLICE_KEY, key, value)
    },
    setStateByKeys: sliceState => {
      get().setAppStateByKeys(SLICE_KEY, sliceState)
    },
    resetState: () => {
      get().resetAppState(SLICE_KEY, {
        ...DEFAULT_STATE,
        formValues: { ...get()[SLICE_KEY].formValues, fromError: '' },
      })
    },
  },
})

function getRouterActiveKey(
  curve: CurveApi | null,
  { fromAmount }: FormValues,
  searchedParams: SearchedParams,
  maxSlippage: string | undefined,
) {
  const { chainId = '', signerAddress = '' } = curve ?? {}
  const { fromAddress, toAddress } = searchedParams

  const parsedSignerAddress = signerAddress.slice(0, 10)
  const parsedFromAddress = fromAddress ? fromAddress.slice(fromAddress.length - 4) : ''
  const parsedToAddress = toAddress ? toAddress.slice(toAddress.length - 4) : ''

  return `${chainId}-${parsedSignerAddress}-${parsedFromAddress}-${parsedToAddress}-${fromAmount}-${maxSlippage}`
}
